<template>
    <!--
        =====================================================================
        Vendor Notification Dialog (spec section 7)
        =====================================================================
        Opens automatically after a completed Stage 3 run. Shows the draft
        email addressed to the vendor's primary contact (resolved by the
        backend from the admin's subnetwork). Admin can Edit the subject +
        body freely, then Send (dispatches via SES) or Do-not-send
        (dismisses; admin will notify through their own channel).
        -->
    <Dialog
        v-model:visible="visibleModel"
        :header="dialogHeader"
        :modal="true"
        :closable="true"
        :draggable="false"
        :style="{ width: '720px', maxWidth: '96vw' }"
        class="vendor-notification-dialog"
        @hide="onHide"
    >
        <div v-if="loading" class="vnd-state">
            <i class="pi pi-spin pi-spinner"></i>
            Preparing notification…
        </div>

        <div v-else-if="loadError" class="vnd-state vnd-state-error">
            <div><i class="pi pi-times-circle"></i> {{ loadError }}</div>
        </div>

        <!-- can_send=false branch: recipient couldn't be resolved -->
        <div v-else-if="preview && preview.can_send === false" class="vnd-state vnd-state-blocked">
            <div class="vnd-blocked-title">
                <i class="pi pi-info-circle"></i>
                Notification can't be sent automatically
            </div>
            <div class="vnd-blocked-body">
                {{ preview.error_message }}
            </div>
            <div class="vnd-blocked-code" v-if="preview.error_code">
                Error code: <code>{{ preview.error_code }}</code>
            </div>
            <div class="vnd-blocked-note">
                The AI Pre-Population is still complete — you can dismiss this dialog
                and notify the vendor through your own channel, or fix the recipient
                resolution and reopen.
            </div>
        </div>

        <!-- can_send=true branch: show the editable draft -->
        <div v-else-if="preview && preview.can_send" class="vnd-form">
            <div class="vnd-recipient-row">
                <span class="vnd-label">To:</span>
                <span class="vnd-recipient-value">
                    {{ preview.recipient.name || 'Vendor contact' }}
                    &lt;{{ preview.recipient.email }}&gt;
                </span>
            </div>

            <div class="vnd-subject-row">
                <label class="vnd-label" for="vnd-subject">Subject:</label>
                <InputText
                    id="vnd-subject"
                    v-model="editableSubject"
                    :disabled="sending"
                    class="vnd-subject-input"
                />
            </div>

            <div class="vnd-body-row">
                <label class="vnd-label">Message:</label>
                <!-- Simple contenteditable rich-text region. Handlebars-safe
                     text + inline styles preserved. On send we pass innerHTML
                     to the backend. Not a full WYSIWYG toolbar — admin can
                     edit copy freely, but formatting stays intentional to
                     avoid breaking the layout. -->
                <div
                    ref="bodyEditor"
                    contenteditable="true"
                    class="vnd-body-editor"
                    :class="{ 'vnd-body-disabled': sending }"
                    @input="onBodyInput"
                    v-html="initialBodyHtml"
                ></div>
                <div class="vnd-body-hint">
                    You can edit any text above before sending. Formatting is preserved.
                </div>
            </div>
        </div>

        <template #footer>
            <div class="vnd-footer" v-if="!loading && !loadError">
                <Button
                    v-if="preview && preview.can_send"
                    label="Do not send"
                    class="p-button-outlined vnd-skip-btn"
                    :disabled="sending || skipping"
                    @click="handleSkip"
                />
                <Button
                    v-else
                    label="Close"
                    class="p-button-outlined"
                    :disabled="skipping"
                    @click="handleClose"
                />
                <Button
                    v-if="preview && preview.can_send"
                    :label="sending ? 'Sending…' : 'Send notification'"
                    icon="pi pi-send"
                    class="p-button-primary vnd-send-btn"
                    :disabled="sending || skipping || !editableSubject.trim()"
                    @click="handleSend"
                />
            </div>
        </template>
    </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import VendorSourceService from '../../service/VendorSourceService';

export default {
    name: 'VendorNotificationDialog',
    components: { Dialog, InputText, Button },
    props: {
        modelValue: { type: Boolean, default: false }, // v-model for open/close
        partnerId: { type: String, required: true },
        runId: { type: String, default: null },
        vendorName: { type: String, default: '' },
    },
    emits: [
        'update:modelValue',
        'sent',       // fires with { run, recipient } after successful send
        'skipped',    // fires with { run } after do-not-send
        'closed',     // fires when dialog closes (no action taken)
    ],
    data() {
        return {
            vendorSourceService: new VendorSourceService(),
            loading: false,
            loadError: null,
            preview: null,          // response from getNotificationPreview
            editableSubject: '',
            editableBodyHtml: '',
            initialBodyHtml: '',    // seeded once; contenteditable manages its own state
            sending: false,
            skipping: false,
        };
    },
    computed: {
        visibleModel: {
            get() {
                return this.modelValue;
            },
            set(v) {
                this.$emit('update:modelValue', v);
            },
        },
        dialogHeader() {
            if (this.loading) return 'AI Pre-Population complete';
            if (this.loadError) return 'Notification';
            if (this.preview && this.preview.can_send === false)
                return 'Notification can\'t be sent';
            return 'Notify vendor to review';
        },
    },
    watch: {
        modelValue(next) {
            if (next && this.runId) {
                this.loadPreview();
            } else if (!next) {
                this.resetState();
            }
        },
    },
    methods: {
        async loadPreview() {
            this.loading = true;
            this.loadError = null;
            this.preview = null;
            try {
                const res = await this.vendorSourceService.getNotificationPreview(
                    this.partnerId,
                    this.runId,
                    this.vendorName
                );
                const data = res?.data?.data;
                if (!data) {
                    this.loadError =
                        res?.data?.message ||
                        'Could not load the notification preview.';
                    return;
                }
                this.preview = data;
                if (data.can_send && data.draft) {
                    this.editableSubject = data.draft.subject || '';
                    this.editableBodyHtml = data.draft.body_html || '';
                    this.initialBodyHtml = data.draft.body_html || '';
                }
            } catch (err) {
                this.loadError = err?.message || 'Could not load the notification preview.';
            } finally {
                this.loading = false;
            }
        },

        onBodyInput(e) {
            this.editableBodyHtml = e.target.innerHTML;
        },

        async handleSend() {
            if (!this.editableSubject.trim() || !this.editableBodyHtml.trim()) {
                this.$toast.add({
                    severity: 'warn',
                    summary: 'Missing content',
                    detail: 'Subject and message body are both required.',
                    life: 3000,
                });
                return;
            }
            this.sending = true;
            try {
                const res = await this.vendorSourceService.sendNotification(
                    this.partnerId,
                    this.runId,
                    this.editableSubject.trim(),
                    this.editableBodyHtml
                );
                const data = res?.data?.data;
                if (data?.run) {
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Notification sent',
                        detail: `Emailed ${data.recipient?.email || 'the vendor'}.`,
                        life: 4000,
                    });
                    this.$emit('sent', data);
                    this.visibleModel = false;
                } else {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Send failed',
                        detail: res?.data?.message || 'Unknown error.',
                        life: 5000,
                    });
                }
            } catch (err) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Send failed',
                    detail: err?.message || 'Unknown error.',
                    life: 5000,
                });
            } finally {
                this.sending = false;
            }
        },

        async handleSkip() {
            this.skipping = true;
            try {
                const res = await this.vendorSourceService.skipNotification(
                    this.partnerId,
                    this.runId
                );
                const data = res?.data?.data;
                if (data?.run) {
                    this.$toast.add({
                        severity: 'info',
                        summary: 'Notification skipped',
                        detail:
                            'Answer Bank is still populated — reach out to the vendor through your own channel.',
                        life: 4000,
                    });
                    this.$emit('skipped', data);
                    this.visibleModel = false;
                } else {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Could not skip',
                        detail: res?.data?.message || 'Unknown error.',
                        life: 5000,
                    });
                }
            } catch (err) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Could not skip',
                    detail: err?.message || 'Unknown error.',
                    life: 5000,
                });
            } finally {
                this.skipping = false;
            }
        },

        handleClose() {
            this.visibleModel = false;
        },

        onHide() {
            // Fires whenever the dialog closes (X, backdrop click, Send,
            // Skip, or Close). resetState() runs via the modelValue
            // watcher too, but firing 'closed' here is the cleanest hook
            // for the parent to know the interaction ended.
            this.$emit('closed');
        },

        resetState() {
            this.loading = false;
            this.loadError = null;
            this.preview = null;
            this.editableSubject = '';
            this.editableBodyHtml = '';
            this.initialBodyHtml = '';
            this.sending = false;
            this.skipping = false;
        },
    },
};
</script>

<style scoped>
.vendor-notification-dialog :deep(.p-dialog-content) {
    padding: 1.5rem;
}

.vnd-state {
    padding: 1.5rem;
    text-align: center;
    color: #6c757d;
    display: flex;
    gap: 0.5rem;
    align-items: center;
    justify-content: center;
}
.vnd-state-error { color: #8a1c14; }
.vnd-state-blocked {
    text-align: left;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
}
.vnd-blocked-title {
    font-weight: 600;
    color: #333;
    font-size: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}
.vnd-blocked-title .pi { color: #8a6500; }
.vnd-blocked-body {
    color: #333;
    line-height: 1.5;
}
.vnd-blocked-code {
    font-size: 0.85rem;
    color: #6c757d;
}
.vnd-blocked-note {
    background: #f8f9fa;
    padding: 0.75rem 1rem;
    border-radius: 6px;
    color: #6c757d;
    font-size: 0.9rem;
    line-height: 1.5;
}

.vnd-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}
.vnd-label {
    font-weight: 600;
    color: #333;
    display: block;
    margin-bottom: 0.35rem;
    font-size: 0.9rem;
}
.vnd-recipient-row {
    background: #f0faf8;
    border: 1px solid #cce8d4;
    border-radius: 6px;
    padding: 0.6rem 0.85rem;
    color: #2e5a39;
    font-size: 0.9rem;
    display: flex;
    gap: 0.5rem;
    align-items: baseline;
}
.vnd-recipient-row .vnd-label {
    margin: 0;
    color: #2e5a39;
}
.vnd-recipient-value {
    flex: 1;
    word-break: break-all;
}

.vnd-subject-input {
    width: 100%;
}

.vnd-body-editor {
    border: 1px solid #d5d9df;
    border-radius: 6px;
    padding: 0.85rem 1rem;
    max-height: 400px;
    overflow-y: auto;
    background: #fff;
    line-height: 1.5;
    font-size: 0.9rem;
    color: #333;
    outline: none;
}
.vnd-body-editor:focus-within {
    border-color: #008a83;
    box-shadow: 0 0 0 3px rgba(0, 138, 131, 0.12);
}
.vnd-body-disabled {
    background: #f8f9fa;
    pointer-events: none;
    opacity: 0.7;
}
.vnd-body-hint {
    color: #6c757d;
    font-size: 0.8rem;
    margin-top: 0.35rem;
}

.vnd-footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
}
.vnd-footer :deep(.p-button) {
    width: auto !important;
    min-width: 8rem;
    white-space: nowrap;
}
.vnd-send-btn {
    background: #008a83 !important;
    border-color: #008a83 !important;
}
.vnd-skip-btn {
    color: #6c757d !important;
    border-color: #d5d9df !important;
}
.vnd-skip-btn:hover:not(:disabled) {
    background: #f8f9fa !important;
}
</style>
